// src/components/ProjectsList.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    doc
} from "firebase/firestore";
import { db } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import { FiPlus, FiLoader, FiSearch, FiFilter, FiLayout, FiAlertCircle, FiUsers } from 'react-icons/fi';
import { getPlatformDisplay } from '../shared/utils/platformHelpers';

// --- Constants ---
const PLATFORMS = ["All", "Meta", "Facebook", "Instagram", "Twitter/X", "LinkedIn"];
const SORT_OPTIONS = {
    'createdAt_desc': 'Newest First',
    'createdAt_asc': 'Oldest First',
    'projectName_asc': 'Name (A-Z)',
    'projectName_desc': 'Name (Z-A)',
};

// --- Helper Functions ---
function formatFirebaseTimestamp(timestamp) {
    if (!timestamp?.toDate) { return 'Unknown Date'; }
    return timestamp.toDate().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', });
}

function getConnectionStatus(project) {
    if (!project) return { text: 'N/A', color: 'text-cream/30', dot: 'bg-cream/20' };
    
    // Handle Meta platform - Active if at least one is connected
    if (project.platform === 'Meta (Facebook & Instagram)') {
        const hasAnyConnection = !!project.associatedPageId || !!project.associatedInstagramId;
        
        if (hasAnyConnection) {
            return { text: 'Active', color: 'text-pastel-green', dot: 'bg-pastel-green' };
        } else {
            return { text: 'Setup Required', color: 'text-soft-coral', dot: 'bg-soft-coral animate-pulse' };
        }
    }
    
    switch (project.platform) {
        case 'Facebook': 
            return project.associatedPageId 
                ? { text: 'Active', color: 'text-pastel-green', dot: 'bg-pastel-green' } 
                : { text: 'Setup Required', color: 'text-soft-coral', dot: 'bg-soft-coral animate-pulse' };
        case 'Instagram': 
            return project.associatedInstagramId 
                ? { text: 'Active', color: 'text-pastel-green', dot: 'bg-pastel-green' } 
                : { text: 'Setup Required', color: 'text-soft-coral', dot: 'bg-soft-coral animate-pulse' };
        default: 
            return { text: 'Draft', color: 'text-cream/30', dot: 'bg-cream/20' };
    }
}

// --- Component ---
function ProjectsList() {
    // --- State ---
    const [allProjects, setAllProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [platformFilter, setPlatformFilter] = useState('All');
    const [sortBy, setSortBy] = useState('createdAt_desc');
    const { userId } = useAuthUser();
    const navigate = useNavigate();

    // --- Team data state ---
    const [userData, setUserData] = useState(null);
    const [teamProjects, setTeamProjects] = useState([]);

    // --- Fetch user doc for team data ---
    useEffect(() => {
        if (!userId) return;
        const userRef = doc(db, "users", userId);
        const unsubscribe = onSnapshot(userRef, (snap) => {
            if (snap.exists()) {
                setUserData(snap.data());
            }
        }, () => {});
        return () => unsubscribe();
    }, [userId]);

    // --- Fetch Projects (Real-time, All) ---
    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const q = query(
            collection(db, "projects"),
            where("userId", "==", userId),
            orderBy("createdAt", "desc") // Default sort
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const projectsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllProjects(projectsData);
            setLoading(false);
        }, (err) => {
            setError("Failed to load projects.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId]);

    // --- Fetch team-shared projects (if user has a teamId) ---
    useEffect(() => {
        const teamId = userData?.teamId;
        if (!userId || !teamId) {
            setTeamProjects([]);
            return;
        }

        const q = query(
            collection(db, "projects"),
            where("teamShared", "==", true),
            where("teamId", "==", teamId)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const projects = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setTeamProjects(projects);
        }, () => {
            setTeamProjects([]);
        });

        return () => unsubscribe();
    }, [userId, userData?.teamId]);

    // --- Merge own + team projects, deduplicate by ID ---
    const mergedProjects = useMemo(() => {
        const map = new Map();
        allProjects.forEach(p => map.set(p.id, p));
        teamProjects.forEach(p => { if (!map.has(p.id)) map.set(p.id, p); });
        return Array.from(map.values());
    }, [allProjects, teamProjects]);

    // --- Client-Side Filtering & Sorting ---
    const filteredProjects = useMemo(() => {
        let result = [...mergedProjects];

        // 1. Platform Filter
        if (platformFilter !== 'All') {
            result = result.filter(p => {
                // Handle 'Meta' filter - only show projects connected to BOTH platforms
                if (platformFilter === 'Meta') {
                    return p.platform === 'Meta (Facebook & Instagram)' && p.associatedPageId && p.associatedInstagramId;
                }
                // Handle 'Facebook' filter - show Facebook-only projects OR Meta projects with only Facebook connected
                if (platformFilter === 'Facebook') {
                    return p.platform === 'Facebook' || 
                           (p.platform === 'Meta (Facebook & Instagram)' && p.associatedPageId && !p.associatedInstagramId);
                }
                // Handle 'Instagram' filter - show Instagram-only projects OR Meta projects with only Instagram connected
                if (platformFilter === 'Instagram') {
                    return p.platform === 'Instagram' || 
                           (p.platform === 'Meta (Facebook & Instagram)' && p.associatedInstagramId && !p.associatedPageId);
                }
                return p.platform === platformFilter;
            });
        }

        // 2. Fuzzy Search (Case-insensitive substring)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            result = result.filter(p => 
                p.projectName?.toLowerCase().includes(term) ||
                p.industry?.toLowerCase().includes(term)
            );
        }

        // 3. Sorting
        result.sort((a, b) => {
            switch (sortBy) {
                case 'createdAt_desc':
                    return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
                case 'createdAt_asc':
                    return (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0);
                case 'projectName_asc':
                    return (a.projectName || '').localeCompare(b.projectName || '');
                case 'projectName_desc':
                    return (b.projectName || '').localeCompare(a.projectName || '');
                default:
                    return 0;
            }
        });

        return result;
    }, [mergedProjects, searchTerm, platformFilter, sortBy]);

    const handleCreateProject = () => { navigate('/create-project'); };

    // --- Render ---
    return (
        <div className="space-y-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10 min-h-[80vh]">
            
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 border-b border-white/5 pb-8">
                <div>
                     <div className="flex items-center gap-2 mb-2">
                         <span className="px-3 py-1 bg-slate-blue/10 rounded-full border border-slate-blue/20 text-[10px] uppercase font-black tracking-widest text-slate-blue">
                             Overview
                         </span>
                     </div>
                    <h1 className="text-5xl font-black text-cream uppercase tracking-tighter drop-shadow-lg">
                        My <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream pr-2">Projects</span>
                    </h1>
                    <p className="text-cream/50 mt-2 font-medium text-lg">Manage your brand deployments and content streams.</p>
                </div>
                <button 
                    onClick={handleCreateProject} 
                    className="btn btn-primary px-8 py-4 text-sm shadow-glow hover:shadow-glow/80 hover:scale-105 active:scale-95 transition-all"
                > 
                    <FiPlus className="mr-2 h-5 w-5" /> 
                    New Project 
                </button> 
            </div>

            {/* Controls Section */}
            <div className="glass-panel p-8 shadow-organic mb-10">
                 <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-cream/40">
                        <FiFilter className="text-slate-blue" /> Filter & Sort
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6"> 
                    <div className="relative group"> 
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"> 
                            <FiSearch className="h-4 w-4 text-cream/30 group-focus-within:text-slate-blue transition-colors" /> 
                        </div> 
                        <input 
                            type="search" 
                            className="w-full pl-10 pr-4 py-4 text-sm bg-black/20 border border-white/5 rounded-2xl focus:ring-1 focus:ring-slate-blue focus:border-slate-blue text-cream placeholder-cream/20 transition-all shadow-inner focus:bg-black/30" 
                            placeholder="Search projects..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                        /> 
                    </div> 
                    
                    <div> 
                        <select 
                            className="form-select text-sm py-4 rounded-2xl bg-black/20 text-cream border-white/5" 
                            value={platformFilter} 
                            onChange={(e) => setPlatformFilter(e.target.value)} 
                        > 
                            {PLATFORMS.map(platform => ( <option key={platform} value={platform}> {platform === 'All' ? 'All Platforms' : platform} </option> ))} 
                        </select> 
                    </div> 
                    
                    <div> 
                        <select 
                            className="form-select text-sm py-4 rounded-2xl bg-black/20 text-cream border-white/5" 
                            value={sortBy} 
                            onChange={(e) => setSortBy(e.target.value)} 
                        > 
                            {Object.entries(SORT_OPTIONS).map(([value, label]) => ( <option key={value} value={value}>{label}</option> ))} 
                        </select> 
                    </div> 
                </div>
            </div>

            {/* Error Display */}
            {error && ( 
                <div className="p-6 bg-soft-coral/10 border border-soft-coral/20 text-soft-coral rounded-3xl flex items-center gap-3"> 
                    <FiAlertCircle size={20} />
                    <p className="font-medium text-sm">{error}</p> 
                </div> 
            )}

            {/* Loading State */}
            {loading && allProjects.length === 0 && ( 
                <div className="text-center py-20 flex flex-col items-center"> 
                    <FiLoader className="animate-spin h-10 w-10 text-slate-blue mb-4" /> 
                    <p className="text-cream/40 text-xs font-bold uppercase tracking-widest animate-pulse">Retrieving Projects...</p> 
                </div> 
            )}

            {/* Empty State */}
            {!loading && filteredProjects.length === 0 && !error && ( 
                <div className="text-center py-24 px-8 glass-panel border-dashed border-2 border-white/5 flex flex-col items-center"> 
                    <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mb-8 text-cream/20">
                        <FiLayout size={40} />
                    </div>
                    <h3 className="text-2xl font-black text-cream uppercase tracking-tight mb-2">No projects found</h3> 
                    <p className="text-cream/50 text-base mb-10 max-w-md mx-auto"> 
                        {searchTerm ? 'No projects match your search.' : 'Launch a new brand project to get started.'} 
                    </p> 
                    <button onClick={handleCreateProject} className="btn btn-secondary px-8 py-3 text-sm border border-white/10 hover:bg-white/10 shadow-none"> 
                        Launch Project 
                    </button> 
                </div> 
            )}

            {/* Projects Grid */}
            {!loading && filteredProjects.length > 0 && ( 
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fade-in"> 
                    {filteredProjects.map((project) => { 
                        if (!project || !project.id) return null; 
                        const status = getConnectionStatus(project); 
                        const projectName = project.projectName || 'Unnamed Project'; 
                        const platformDisplay = getPlatformDisplay(project);
                        
                        return ( 
                            <Link 
                                to={`/project/${project.id}`} 
                                key={project.id} 
                                className="group bg-warm-gray/10 backdrop-blur-md border border-white/5 rounded-[2.5rem] overflow-hidden hover:border-slate-blue/50 hover:shadow-organic hover:bg-warm-gray/20 hover:-translate-y-1 transition-all duration-300 flex flex-col h-full relative"
                            > 
                                {/* Cover Image Area */}
                                <div className="h-48 bg-black/20 relative overflow-hidden group-hover:opacity-90 transition-opacity">
                                    {project.coverImageUrl ? (
                                        <div 
                                            className="w-full h-full bg-cover bg-center opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                                            style={{ backgroundImage: `url(${project.coverImageUrl})` }}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-warm-gray/20 to-warm-dark">
                                            <span className="text-6xl font-black text-white/5 uppercase select-none group-hover:text-slate-blue/10 transition-colors">NI</span>
                                        </div>
                                    )}

                                    
                                     {/* Status Dot (Absolute) */}
                                    <div className="absolute top-5 left-5">
                                         <div className={`w-2.5 h-2.5 rounded-full ${status.dot} shadow-sm`}></div>
                                    </div>
                                </div>
                                
                                {/* Content Area */}
                                <div className="p-7 flex-1 flex flex-col">
                                    <div className="mb-6">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-lg font-black text-cream uppercase tracking-tight truncate group-hover:text-slate-blue transition-colors leading-tight">{projectName}</h3>
                                            {project.userId !== userId && (
                                                <span className="text-[9px] font-bold text-slate-blue bg-slate-blue/10 px-2 py-0.5 rounded-full border border-slate-blue/20 uppercase tracking-wider flex items-center gap-1 shrink-0">
                                                    <FiUsers size={9} /> Team
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-cream/40 font-mono tracking-wide uppercase">Used {formatFirebaseTimestamp(project.lastAccessedAt || project.createdAt)}</p>
                                    </div>
                                    
                                    <div className="mt-auto pt-5 border-t border-white/5 flex items-center justify-between">
                                        {status.text !== 'Draft' && (
                                            <span className={`text-[10px] uppercase font-bold tracking-wider ${status.color}`}>{status.text}</span>
                                        )}
                                        <span className={`text-xs font-bold ${platformDisplay.color} bg-white/5 px-2 py-1 rounded-md ${status.text === 'Draft' ? 'ml-auto' : ''}`}>{platformDisplay.name}</span>
                                    </div>
                                </div>
                            </Link> 
                        ); 
                    })} 
                </div> 
            )}
        </div>
    );
}

export default ProjectsList;
